import { useQuery } from '@tanstack/react-query';
import { Download, Landmark } from 'lucide-react';
import { api } from '@/lib/api';
import { baixarCsv, carimboArquivo, type ColunaCsv } from '@/lib/csv';
import { dataCurta, moeda, numero, plural } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { Esqueleto } from '@/componentes/ui/primitivos';

/**
 * A contabilização da Integração de Estoque do escopo — a consulta
 * CONTABILIZACAO_INVENTARIO do PBS, que lá só abria de dentro do formulário.
 * O lote contábil roda depois da integração; até rodar, não há lançamento.
 */
type Linha = Record<string, unknown>;

interface Resposta {
  integracoes: number[];
  colunas: string[];
  linhas: Linha[];
}

const texto = (v: unknown): string => (v === null || v === undefined ? '' : String(v).trim());
const valor = (v: unknown): number => (typeof v === 'number' ? v : Number(v ?? 0) || 0);

const COLUNAS_CSV: ColunaCsv<Linha>[] = [
  { cabecalho: 'Lançamento', valor: (l) => texto(l.LANCAMENTO), texto: true },
  { cabecalho: 'Data', valor: (l) => (l.DATA ? dataCurta(String(l.DATA)) : '') },
  { cabecalho: 'Conta débito', valor: (l) => texto(l.CONTA_DEBITO), texto: true },
  { cabecalho: 'Descrição débito', valor: (l) => texto(l.DESCRICAO_DEBITO) },
  { cabecalho: 'Conta crédito', valor: (l) => texto(l.CONTA_CREDITO), texto: true },
  { cabecalho: 'Descrição crédito', valor: (l) => texto(l.DESCRICAO_CREDITO) },
  { cabecalho: 'Valor', valor: (l) => valor(l.VALOR).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) },
  { cabecalho: 'Complemento', valor: (l) => texto(l.COMPLEMENTO) },
];

export function PainelContabilizacao({ inventario, escopo }: { inventario: number; escopo: number }): JSX.Element {
  const consulta = useQuery({
    queryKey: ['inventario', inventario, 'escopo', escopo, 'contabilizacao'],
    queryFn: () => api.get<Resposta>(`/api/inventarios/${inventario}/escopos/${escopo}/contabilizacao`),
    staleTime: 60_000,
  });
  const linhas = consulta.data?.linhas ?? [];
  const total = linhas.reduce((s, l) => s + valor(l.VALOR), 0);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-semibold">
          <Landmark className="h-4 w-4 text-textoFraco" />
          Contabilização da integração
        </p>
        {linhas.length > 0 ? (
          <Botao
            variante="fantasma"
            tamanho="micro"
            onClick={() => baixarCsv(`contabilizacao-escopo-${escopo}-${carimboArquivo()}`, linhas, COLUNAS_CSV)}
          >
            <Download className="h-3.5 w-3.5" />
            Excel
          </Botao>
        ) : null}
      </div>
      {consulta.isLoading ? (
        <Esqueleto className="h-16" />
      ) : consulta.isError ? (
        <p className="text-xs text-status-divergencia">Não foi possível ler a contabilização.</p>
      ) : linhas.length === 0 ? (
        <p className="rounded-lg bg-superficie2 px-3 py-2 text-xs text-textoFraco">
          Ainda sem lançamento contábil. O lote contábil do ERP roda depois da integração — volte aqui depois dele.
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-borda">
          <table className="w-full text-xs">
            <thead className="bg-superficie2 text-left text-textoFraco">
              <tr>
                <th className="px-2.5 py-1.5 font-medium">Data</th>
                <th className="px-2.5 py-1.5 font-medium">Débito</th>
                <th className="px-2.5 py-1.5 font-medium">Crédito</th>
                <th className="px-2.5 py-1.5 text-right font-medium">Valor</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((l, i) => (
                <tr key={`${texto(l.LANCAMENTO)}-${i}`} className="border-t border-borda align-top">
                  <td className="whitespace-nowrap px-2.5 py-1.5">{l.DATA ? dataCurta(String(l.DATA)) : ''}</td>
                  <td className="px-2.5 py-1.5">
                    <span className="block font-mono">{texto(l.CONTA_DEBITO)}</span>
                    <span className="text-2xs text-textoFraco">{texto(l.DESCRICAO_DEBITO)}</span>
                  </td>
                  <td className="px-2.5 py-1.5">
                    <span className="block font-mono">{texto(l.CONTA_CREDITO)}</span>
                    <span className="text-2xs text-textoFraco">{texto(l.DESCRICAO_CREDITO)}</span>
                  </td>
                  <td className="tabular whitespace-nowrap px-2.5 py-1.5 text-right font-semibold">
                    {moeda(valor(l.VALOR))}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-borda bg-superficie2">
                <td colSpan={3} className="px-2.5 py-1.5 text-textoFraco">
                  {numero(linhas.length)} {plural(linhas.length, 'lançamento', 'lançamentos')}
                </td>
                <td className="tabular px-2.5 py-1.5 text-right font-bold">{moeda(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
